import { QueryResult } from '../../../request/hooks/useQuery';
import { BackendSubscriptionAction } from '../../../request/interfaces/backend-subscription';
import { Job } from '../../interfaces/job';
import { JobsAPI } from '../../JobsAPI';
import { JobsTable } from './JobsTable/JobsTable';
import { EntityList } from '../../../entity-list/EntityList';
import { ListMetaNetworkParams } from '../../../list-conductor/list-meta-data';
import { JobsIntro } from './JobsIntro';
import { EntityListEntities } from '../../../shared/interfaces/entity-list';
import { JOBS_FILTERS } from '../../constants/job-filters';
import { Filters } from '../../../filter/Filters';
import { DEFAULT_SORT } from '../../constants/constants';
import styles from './JobsView.module.scss';
import { GenericStatus } from '../../../shared/interfaces/generic-status';
import { IntegrationStatus, PipelineConfig } from '../../../shared/interfaces/integration';
import { SyncType } from '../../../pipelines/interfaces/pipeline-settings-config';

export interface JobsViewProps {
  integrationId?: number;
  integrationStatus?: GenericStatus<IntegrationStatus>;
  integrationConfig?: PipelineConfig;
}

export function JobsView({ integrationId, integrationStatus, integrationConfig }: JobsViewProps) {
  const isPipelineDisabled =
    integrationStatus?.value === IntegrationStatus.DISABLED ||
    integrationConfig?.sync_type === SyncType.ON_DEMAND;

  const fetch = (params?: ListMetaNetworkParams): Promise<QueryResult<Array<Job>>> => {
    if (!integrationId) {
      return JobsAPI.getAllJobs(params).then(res => {
        return {
          responseMetaData: {
            count: res.total
          },
          items: res.data
        };
      });
    }

    return JobsAPI.getPipelineJobs(integrationId, params).then(res => ({
      responseMetaData: {
        count: res.total
      },
      items: res.data
    }));
  };

  const refetch = (currentJobs?: Job[]): Promise<Job[]> => {
    const jobIds = (currentJobs || []).map(job => job.jobId);

    if (!integrationId || !jobIds.length) {
      return Promise.resolve([]);
    }

    return JobsAPI.getPipelineJobs(integrationId, {
      filters: [
        {
          fields: ['job_id'],
          operator: 'IN',
          values: jobIds
        }
      ]
    }).then(res =>
      res.data.sort((a, b) => {
        return jobIds.indexOf(a.jobId) - jobIds.indexOf(b.jobId);
      })
    );
  };

  return (
    <EntityList
      entity={EntityListEntities.JOBS_VIEW}
      mode={integrationId ? 'connected' : 'standalone'}
      dataSource$={fetch}
      pollDataSource$={refetch}
      listIcon='jobs'
      entityTitle='Jobs'
      showFilters
      showSearch={false}
      emptyTitle='No Job Found'
      emptySubTitle='Modify search term to find the desired Job'
      showRefreshInNewList={!isPipelineDisabled}
      shimmerColumnsCount={7}
      defaultListMeta={{ filters: [], sort: DEFAULT_SORT }}
      newListConnectedTitle={isPipelineDisabled ? 'No jobs found' : 'Loading Jobs'}
      NewListComponent={JobsIntro}
      FiltersComponent={Filters}
      genericErrorTitle='Error while fetching Jobs'
      genericErrorSubTitle='Retry Loading the Jobs'
      ListComponent={JobsTable}
      listComponentProps={{
        withPipelineDetails: !integrationId
      }}
      filtersComponentProps={{
        filters: JOBS_FILTERS,
        entity: !integrationId ? 'jobs-list' : 'pipeline-jobs-list'
      }}
      classes={{ listClassName: styles.jobList }}
      subscriptionQueries={
        integrationId
          ? [
              {
                event: 'PIPELINE_STATUS_UPDATED',
                ids: [integrationId],
                action: BackendSubscriptionAction.UPDATE
              }
            ]
          : []
      }
    />
  );
}
